import { Button, FormField, Tabs } from '../ui';
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { agentLabel } from '../../lib/format';

/**
 * Редактор агента: форма для промпта и инструментов, JSON — для остального.
 *
 * Здесь то же главное решение, что в SkillEditor: спека — ОДИН объект. Две
 * вкладки со своим состоянием каждая — классический способ молча потерять
 * правку (дописал инструмент в форме, ушёл в JSON, вернулся — инструмента
 * нет). Поэтому форма правит `spec` напрямую, а вкладка JSON — это текстовое
 * представление, которое разбирается обратно при каждом уходе с неё; не
 * разобралось — уйти нельзя, и сказано почему.
 *
 * Slug заблокирован при правке: он — ключ идентичности всех артефактов,
 * которые агент уже произвёл (`produced_by`), и всех адресованных ему задач.
 * Сервер переименование отвергает, а поле, в которое можно вписать
 * заведомо обречённую правку, хуже выключенного.
 *
 * Встроенные — только на просмотр, с кнопкой «Форк»: правка на месте живёт
 * ровно до следующего сида. Форк — ещё и способ сравнить две версии одного
 * промпта на eval: две строки, два агента, один прогон.
 */

const EMPTY_SPEC = {
  slug: '', display_name: '', department: 'business_lead',
  system_prompt: '', artifact_type: '', tools: [],
  model_profile: 'drafter', max_output_tokens: 4096, max_tool_steps: 6,
  version: 1, notes: '',
};

function errorText(e, fallback) {
  const detail = e?.response?.data?.detail || e?.response?.data?.error?.message;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
  return e?.message || fallback;
}

export default function AgentEditor({ slug, options, onClose, onSaved }) {
  const isNew = !slug;
  const [row, setRow] = useState(null);
  const [spec, setSpec] = useState(isNew ? EMPTY_SPEC : null);
  const [tab, setTab] = useState('form');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [bundleRevision, setBundleRevision] = useState(null);
  useEffect(() => { if (!isNew) return; api.getPrompts().then(value => setBundleRevision(value.revision)).catch(e => setLoadError(errorText(e, 'Не удалось загрузить версию инструкций'))); }, [slug, isNew]);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    api.getAgentSpec(slug)
      .then(data => {
        if (cancelled) return;
        setRow(data);
        if (data.base_bundle_revision) setBundleRevision(data.base_bundle_revision);
        setSpec(data.spec);
      })
      .catch(e => { if (!cancelled) setLoadError(errorText(e, 'Не удалось загрузить агента')); });
    return () => { cancelled = true; };
  }, [slug, isNew]);

  const readOnly = Boolean(row?.is_builtin);
  const opts = options || {};
  const implicit = opts.implicit_tools || [];

  const patch = (changes) => setSpec(prev => ({ ...prev, ...changes }));

  const toggleTool = (tool) => {
    const have = spec.tools || [];
    patch({ tools: have.includes(tool) ? have.filter(t => t !== tool) : [...have, tool] });
  };

  /** Уход с вкладки JSON: разобрать текст обратно в спеку или не пустить. */
  const switchTab = (next) => {
    if (tab === 'json' && next !== 'json') {
      try {
        setSpec(JSON.parse(jsonText));
        setJsonError(null);
      } catch (e) {
        setJsonError(
          `JSON не разбирается: ${e.message}. Исправьте или закройте редактор без сохранения.`
        );
        return false;
      }
    }
    if (next === 'json') setJsonText(JSON.stringify(spec, null, 2));
    setTab(next);
    return true;
  };

  const problems = useMemo(() => {
    if (!spec) return [];
    const out = [];
    if (!spec.slug?.trim()) out.push('Не задан slug — по нему адресуются задачи.');
    if (!spec.display_name?.trim()) out.push('Не задано отображаемое имя.');
    if (!spec.system_prompt?.trim()) {
      // Без промпта агент — это вызов модели без задачи.
      out.push('Пустой системный промпт: агенту нечего делать.');
    }
    if (!spec.artifact_type) out.push('Не выбран тип артефакта — иначе результат никто не прочитает.');
    return out;
  }, [spec]);

  const save = async () => {
    if (tab === 'json') { switchTab('form'); return; }
    setBusy(true);
    setStatus(null);
    try {
      const saved = isNew
        ? await api.createAgentSpec(spec, bundleRevision)
        : await api.updateAgentSpec(slug, { spec, notes: spec.notes || '', base_bundle_revision: bundleRevision });
      onSaved?.(saved, { forked: false });
    } catch (e) {
      setStatus({ error: errorText(e, 'Не удалось сохранить агента') });
    } finally {
      setBusy(false);
    }
  };

  const fork = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const forked = await api.forkAgentSpec(slug, { base_bundle_revision: bundleRevision });
      onSaved?.(forked, { forked: true });
    } catch (e) {
      setStatus({ error: errorText(e, 'Не удалось сделать форк') });
    } finally {
      setBusy(false);
    }
  };

  if (loadError) return <div className="alert alert-error">{loadError}</div>;
  if (!spec) return <div className="loading">Загрузка агента…</div>;

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="card-title">
            {isNew ? 'Новый агент' : (spec.display_name || agentLabel(spec.slug))}
          </div>
          {!isNew && (
            <div className="text-muted" style={{ fontSize: '0.78rem' }}>
              <code>{spec.slug}</code> · v{row?.version}
              {row?.is_builtin && ' · встроенный'}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {readOnly && (
            <Button className="btn btn-secondary btn-sm" disabled={busy} onClick={fork}>
              Форк
            </Button>
          )}
          {!readOnly && (
            <Button className="btn btn-primary btn-sm" disabled={busy || problems.length > 0}
              onClick={save}>
              Сохранить
            </Button>
          )}
          <Button className="btn btn-secondary btn-sm" onClick={onClose}>Закрыть</Button>
        </div>
      </div>

      {readOnly && (
        <div className="alert alert-info">
          Встроенный агент: правка на месте живёт ровно до следующего деплоя —
          сид вернёт исходный промпт. Сделайте форк и правьте копию. Форк — ещё
          и способ сравнить две версии одного промпта на eval.
        </div>
      )}
      {status?.error && <div className="alert alert-error">{status.error}</div>}
      {jsonError && <div className="alert alert-error">{jsonError}</div>}
      {problems.length > 0 && !readOnly && (
        <div className="alert alert-warning">
          <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
            {problems.map(p => <li key={p}>{p}</li>)}
          </ul>
        </div>
      )}

      <Tabs label="Редактор агента" variant="segmented" value={tab} onChange={switchTab}
        items={[{ key: 'form', label: 'Форма' }, { key: 'json', label: 'JSON' }]} />

      {tab === 'json' ? (
        <textarea aria-label="Спецификация агента в JSON" className="form-input" style={{ minHeight: 420, fontFamily: 'monospace' }}
          value={jsonText} readOnly={readOnly}
          onChange={e => setJsonText(e.target.value)} />
      ) : (
        <>
          <div className="grid-2">
            <div className="form-group">
              <FormField label={<>Slug</>}>
              <input className="form-input" value={spec.slug} disabled={!isNew || readOnly} onChange={e => patch({
                  slug: e.target.value.trim()
                })} />
              </FormField>
              {!isNew && (
                <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                  Переименование запрещено: slug — ключ идентичности артефактов,
                  которые агент уже произвёл.
                </div>
              )}
            </div>
            <FormField label={<>Отображаемое имя</>}>
            <input className="form-input" value={spec.display_name} disabled={readOnly} onChange={e => patch({
                display_name: e.target.value
              })} />
            </FormField>
            <div className="form-group">
              <FormField label={<>Отдел</>}>
              <select className="form-input" value={spec.department} disabled={readOnly} onChange={e => patch({
                  department: e.target.value
                })}>
                              {(opts.departments || []).map(d => <option key={d} value={d}>{agentLabel(d)} ({d})</option>)}
                            </select>
              </FormField>
              <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                Голова этого отдела и будет ставить агенту задачи. Отдел, до
                которого никто не доходит, — агент, который никогда не запустится.
              </div>
            </div>
            <FormField label={<>Тип артефакта</>}>
            <select className="form-input" value={spec.artifact_type} disabled={readOnly} onChange={e => patch({
                artifact_type: e.target.value
              })}>
                            <option value="">— выберите —</option>
                            {(opts.artifact_types || []).map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
            </FormField>
            <FormField label={<>Профиль модели</>}>
            <select className="form-input" value={spec.model_profile} disabled={readOnly} onChange={e => patch({
                model_profile: e.target.value
              })}>
                            {(opts.model_profiles || []).map(p => <option key={p} value={p}>{p}</option>)}
                          </select>
            </FormField>
            <FormField label={<>Шагов инструментов</>}>
            <input className="form-input" type="number" min={1} max={20} value={spec.max_tool_steps} disabled={readOnly} onChange={e => patch({
                max_tool_steps: Number(e.target.value)
              })} />
            </FormField>
          </div>

          <FormField label={<>Системный промпт</>}>
          <textarea className="form-input" style={{
              minHeight: 260
            }} value={spec.system_prompt} readOnly={readOnly} onChange={e => patch({
              system_prompt: e.target.value
            })} />
          </FormField>

          <div className="form-group">
            <label className="form-label">Инструменты</label>
            <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
              {(opts.tools || []).map(tool => {
                const forced = implicit.includes(tool);
                const checked = forced || (spec.tools || []).includes(tool);
                return (
                  <label key={tool} style={{
                    display: 'flex', gap: 6, alignItems: 'center',
                    cursor: forced ? 'default' : 'pointer',
                    opacity: forced ? 0.65 : 1,
                  }}>
                    <input type="checkbox" checked={checked}
                      disabled={readOnly || forced}
                      onChange={() => toggleTool(tool)} />
                    <code>{tool}</code>
                  </label>
                );
              })}
            </div>
            <div className="text-muted" style={{ fontSize: '0.78rem' }}>
              {implicit.join(', ')} выдаются всегда: агент, который не видит
              артефактов сессии, не может сделать ни одну работу этой платформы,
              а <code>report_blocker</code> — его единственная альтернатива тому,
              чтобы выдумать недостающие данные.
            </div>
          </div>

          <FormField label={<>Заметки</>}>
          <input className="form-input" value={spec.notes || ''} disabled={readOnly} onChange={e => patch({
              notes: e.target.value
            })} />
          </FormField>
        </>
      )}
    </div>
  );
}
